import * as React from "react";
import { toast } from "sonner";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { captureBrowserException } from "@/lib/sentry-browser";

interface State {
  error: Error | null;
  errorId: string | null;
  retries: number;
  autoRetried: boolean;
}

interface Props {
  children: React.ReactNode;
  label?: string;
  /** Force a remount on this key when the boundary resets (useful for wizards). */
  resetKey?: string | number;
}

// Short, copyable error id: AROS-{8-char hex}.
function makeErrorId(): string {
  const rand = Math.floor(Math.random() * 0xffffffff)
    .toString(16)
    .padStart(8, "0")
    .toUpperCase();
  return `AROS-${rand}`;
}

const TRANSIENT_PATTERNS = [
  /Loading chunk \d+ failed/i,
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i,
  /NetworkError/i,
  /Importing a module script failed/i,
];

function isTransient(error: Error): boolean {
  const msg = `${error.name} ${error.message}`;
  return TRANSIENT_PATTERNS.some((rx) => rx.test(msg));
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, errorId: null, retries: 0, autoRetried: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, errorId: makeErrorId() };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const label = this.props.label ?? "App";
    const errorId = this.state.errorId ?? makeErrorId();
    console.error(`[ErrorBoundary ${errorId}]`, label, error, info);

    // Friendly in-app alert
    try {
      toast.error(`${label} hit a snag`, {
        id: errorId,
        description: `${error.message || "An unexpected error occurred."}\nError ID: ${errorId}`,
        duration: 8000,
      });
    } catch {
      /* sonner not mounted yet — swallow */
    }

    // Report to monitoring
    reportLovableError(error, { boundary: label, errorId, componentStack: info.componentStack });
    captureBrowserException(error, {
      boundary: label,
      errorId,
      componentStack: info.componentStack,
    });

    // Auto-retry once for transient chunk/network failures.
    if (!this.state.autoRetried && this.state.retries === 0 && isTransient(error)) {
      this.setState({ autoRetried: true });
      window.setTimeout(() => this.reset(), 600);
    }
  }

  reset = () => {
    this.setState((s) => ({ error: null, errorId: null, retries: s.retries + 1 }));
  };

  render() {
    const { error, errorId } = this.state;
    if (!error) {
      // Keying the subtree on retries forces a clean remount after reset.
      return <React.Fragment key={this.state.retries}>{this.props.children}</React.Fragment>;
    }
    const transient = isTransient(error);
    return (
      <div
        role="alert"
        className="m-4 rounded-lg border border-destructive/40 bg-destructive/10 p-6 text-sm"
      >
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="text-base font-semibold text-destructive">
            {this.props.label ?? "Something went wrong"}
          </div>
          {errorId && (
            <code className="rounded bg-background/60 px-2 py-0.5 text-[11px] font-mono text-muted-foreground">
              {errorId}
            </code>
          )}
        </div>
        <div className="mb-3 font-mono text-destructive/90 break-words">{error.message}</div>
        <p className="mb-3 text-muted-foreground">
          {transient
            ? "Looks like a temporary network hiccup. We tried to recover automatically."
            : "You can keep using the rest of the app. Try again, or reload if it keeps happening."}{" "}
          Share the error ID above when reporting.
        </p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={this.reset}
            className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
          >
            Try again
          </button>
          <button
            onClick={() => typeof window !== "undefined" && window.location.reload()}
            className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
          >
            Reload page
          </button>
          {errorId && (
            <button
              onClick={() => {
                try {
                  navigator.clipboard?.writeText(errorId);
                  toast.success("Error ID copied");
                } catch {
                  /* ignore */
                }
              }}
              className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
            >
              Copy error ID
            </button>
          )}
        </div>
      </div>
    );
  }
}
